/* eslint-disable no-else-return */
/* eslint-disable no-unsafe-optional-chaining */

// Cache for expensive calculations
const widthCache = new Map<string, number>();
const optionsCache = new Map<string, any[]>();

const calculateWidth = (totalElements: number, totalWidth: number) => {
  const cacheKey = `${totalElements}-${totalWidth}`;
  if (widthCache.has(cacheKey)) {
    return widthCache.get(cacheKey)!;
  }

  const nameField: number = 150;
  const columnsWidth = totalElements * 150 + nameField;
  const result = totalWidth > columnsWidth ? totalWidth / (totalElements + 1) : 150;

  widthCache.set(cacheKey, result);
  return result;
};

// Optimized function to get unique options
const getUniqueOptions = (items: any[], key: string): any[] => {
  const cacheKey = `${key}-${items.length}`;
  if (optionsCache.has(cacheKey)) {
    return optionsCache.get(cacheKey)!;
  }

  const options = items
    .filter((item: any) => item?.[key] !== null && item?.[key] !== undefined)
    .reduce((accumulator: any, item: any) => {
      if (!accumulator.includes(item[key])) {
        accumulator.push(item[key]);
      }
      return accumulator;
    }, []);

  optionsCache.set(cacheKey, options);
  return options;
};

// Clear cache when data changes significantly
const clearCache = () => {
  widthCache.clear();
  optionsCache.clear();
};

const headerRow = (tableItemsProperty_: any, tableProperty_: any) => {
  // Early return for empty data
  if (!tableItemsProperty_?.length) {
    return {
      rowId: 'header',
      height: 50,
      cells: [
        {
          type: 'filter',
          options: [],
          id: 'name',
          text: 'Name',
          hasDelete: false,
          hasEdit: false,
          nonEditable: true,
          className: 'header-cell header-first',
        },
      ],
    };
  }

  const nameOptions = getUniqueOptions(tableItemsProperty_, 'name');

  const cells = [
    {
      type: 'filter',
      options: nameOptions,
      id: 'name',
      text: 'Name',
      hasDelete: false,
      hasEdit: false,
      nonEditable: true,
      className: 'header-cell header-first',
    },
  ];

  // Optimize property mapping
  if (tableProperty_?.length) {
    const propertyCells = tableProperty_.map((data: any) => {
      const options = getUniqueOptions(tableItemsProperty_, data?.id);
      return {
        type: 'filter',
        options,
        id: data?.id,
        hasDelete: true,
        hasEdit: true,
        colType: 'header',
        text: data?.name,
        className: 'header-cell',
      };
    });
    cells.push(...propertyCells);
  }

  return {
    rowId: 'header',
    height: 50,
    cells,
  };
};

const footerRow = (tableProperty_: any) => {
  const cells = [
    {
      type: 'addRow',
      text: '',
      className: 'footer-first',
      nonEditable: false,
      options: [],
      id: 'footer',
      hasDelete: false,
      hasEdit: false,
      hasFormulaError: false,
    },
  ];

  // Optimize footer cell creation
  if (tableProperty_?.length) {
    const footerCells = new Array(tableProperty_.length).fill(null).map(() => ({
      type: 'text',
      text: '',
      nonEditable: true,
      className: 'footer',
      hasFormulaError: false,
      options: [],
      id: 'footer-cell',
      hasDelete: false,
      hasEdit: false,
    }));
    cells.push(...footerCells);
  }

  return {
    rowId: 'footer',
    height: 50,
    cells,
  };
};

// Pre-computed date field names for faster comparison
const DATE_FIELD_NAMES = new Set(['start', 'end', 'start date', 'end date']);

const isDateField = (fieldName: string): boolean => {
  return DATE_FIELD_NAMES.has(fieldName.trim().toLowerCase());
};

const getRows = (tableItemsProperty_: any, tableProperty_: any) => {
  // Early return for empty data
  if (!tableItemsProperty_?.length) {
    return [headerRow(tableItemsProperty_, tableProperty_), footerRow(tableProperty_)];
  }

  const rows = [headerRow(tableItemsProperty_, tableProperty_)];

  // Optimize row creation with pre-allocated arrays
  const dataRows = tableItemsProperty_.map((item: any, index: any) => {
    const cells = [
      {
        type: 'text',
        placeholder: 'Enter value',
        text: `${item?.name}`,
        property: 'name',
        rows: tableItemsProperty_.length,
        className: 'name',
        hasFormulaError: false,
        index,
      },
    ];

    // Optimize property cell creation
    if (tableProperty_?.length) {
      const propertyCells = tableProperty_.map((data: any) => {
        if (isDateField(data?.name)) {
          return {
            type: 'date',
            text: item[`${data?.id}`] || '',
            date: new Date(),
            nonEditable: !item?.id,
          };
        } else if (data?.items) {
          return {
            type: 'dropdown',
            usePortal: true,
            nonEditable: !item?.id,
            selectOption: true,
            selectedValue: item[`${data?.id}`],
            values: data?.items?.length
              ? data.items.map((value: any) => ({
                  label: value?.name,
                  value: value?.name,
                }))
              : [],
            index,
            operation: '',
            data,
          };
        } else {
          return {
            type: 'text',
            rows: tableItemsProperty_.length,
            text: item[`${data?.id}`],
            index,
            data,
            hasFormulaError: false,
            nonEditable: !item?.id,
          };
        }
      });
      cells.push(...propertyCells);
    }

    return {
      rowId: index,
      height: 50,
      itemId: item?.id,
      itemName: item?.name,
      reorderable: true,
      cells,
    };
  });

  rows.push(...dataRows, footerRow(tableProperty_));
  return rows;
};

const getColumns = (tableProperty_: any, tableReference: any) => {
  const columns = [
    {
      columnId: 'name',
      width:
        tableReference || tableProperty_
          ? calculateWidth(tableProperty_?.length, tableReference)
          : 200,
      resizable: true,
    },
  ];

  // Optimize column creation
  if (tableProperty_?.length) {
    const propertyColumns = tableProperty_.map((data: any) => ({
      columnId: `${data?.id}`,
      name: data?.name,
      width: tableReference ? calculateWidth(tableProperty_?.length, tableReference) : 200,
      resizable: true,
      reorderable: true,
    }));
    columns.push(...propertyColumns);
  }

  return columns;
};

// Optimized reorder array function
const reorderArray = <T extends {}>(array: T[], idxs: number[], to: number) => {
  if (!array?.length || !idxs?.length) return [];

  // Use Set for faster lookups
  const indexSet = new Set(idxs);
  const movedElements = array.filter((_, index) => indexSet.has(index));

  const minIndex = Math.min(...idxs);
  const targetIndex = minIndex < to ? to + 1 : to - idxs.filter((index) => index < to).length;

  const leftSide = array.filter((_, index) => index < targetIndex && !indexSet.has(index));
  const rightSide = array.filter((_, index) => index >= targetIndex && !indexSet.has(index));

  const reorderedArray = [...leftSide, ...movedElements, ...rightSide];
  return reorderedArray.map((item: any) => item.id);
};

export { headerRow, footerRow, getRows, getColumns, reorderArray, clearCache };
